import { useAIToolLibrary } from "@mirage/mosaics/AIToolLibrary/hooks/useAIToolLibrary";
import { useAIToolLibraryModal } from "@mirage/mosaics/AIToolLibrary/hooks/useAIToolLibraryModal";
import type { ClickableEmptyChatInstructionsOption } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { getUserMessageOptionsFromWorkflowAgent } from "@mirage/mosaics/Chat/components/chat/instructions/initializeWorkflowAgentConversation";
import { StartingMessageNode } from "@mirage/mosaics/Chat/components/chat/instructions/StartingMessageNode";
import { ChatGrid } from "@mirage/mosaics/Chat/components/layouts/ChatPageLayout";
import { SidePanelContainer } from "@mirage/mosaics/ComposeAssistant/components/side-panel/SidePanelContainer";
import { useComposeAnalyticsContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeAnalyticsContext";
import { useComposeCurrentSessionContext } from "@mirage/mosaics/ComposeAssistant/data/current-session/ComposeCurrentSessionContext";
import { useOnboardingMode } from "@mirage/mosaics/ComposeAssistant/onboarding/useOnboardingMode";
import { ChatDiagnosticsViewer } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/diagnostics/ChatDiagnosticsViewer";
import { useFSSOpsHandling } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/FSS/useFSSOpsHandling";
import { useAddSources } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useAddSources";
import { useAgentPayload } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useAgentPayloads";
import { useResetConversationState } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useResetConversationState";
import { useRouteHandling } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useRouteHandling";
import { useWorkflowAgentHandling } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useWorkflowAgentHandling";
import { useWriteHandling } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useWriteHandling";
import { useGetConversationAttachments } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useConversationAttachments";
import {
  useManuallyAddedSources,
  useScopedSources,
} from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import { useConversationPageLoadingStates } from "@mirage/mosaics/ConversationAssistant/hooks/useConversationPageLoading";
import { useTranscribeFiles } from "@mirage/mosaics/ConversationAssistant/hooks/useTranscribeFiles";
import { useCloseOmniboxSearchSession } from "@mirage/mosaics/Omnibox/hooks/useCloseOmniboxSearchSession";
import { postUserMessage } from "@mirage/service-conversation";
import { useConversationRouterState } from "@mirage/service-conversation/hooks/useConversationRouterState";
import type { ConversationRouterState } from "@mirage/service-conversation/router_state_types";
import { ConversationType } from "@mirage/service-conversation/router_state_types";
import {
  type DocumentInfo,
  type ModelId,
  type UserMessageOptions,
  type WorkflowAgent,
} from "@mirage/service-conversation/types";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/utils/loggingUtils";
import {
  DashSearch,
  NoSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { tagged } from "@mirage/service-logging";
import { DashSpinnerAutofitContainer } from "@mirage/shared/dash-spinner/DashSpinnerAutofitContainer";
import { useIsSmallSizeForSidebar } from "@mirage/shared/responsive/mobile";
import { RoutePath } from "@mirage/webapp/routeTypes";
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { StackChatPanel } from "../components/stack-chat/StackChatPanel";
import { useConversationProgress } from "../hooks/progress/useConversationProgress";
import type { SelectedTemplate } from "../hooks/useConversationDraftConfig";
import { useFilteredMessages } from "../hooks/useFilteredMessages";
import { useFssConversationActions } from "../hooks/useFssConversationActions";
import { ConversationAssistantPageContentSidePanelButtons } from "./ConversationAssistantPageContentSidePanelButtons";
import { ConversationChat } from "./ConversationChat";

const logger = tagged("ConversationAssistantPageContent");

export interface ConversationAssistantPageContentProps {
  variant?: "default" | "condensed" | "workflow-agent-preview";
  shouldUseRouterNavigate?: boolean;
  hideTitle?: boolean;
  leftTitleActions?: React.ReactNode;
  rightTitleActions?: React.ReactNode;
  emptyState?: React.ReactNode;
  getTransientSource?: () => Promise<DocumentInfo>;
  onCloseWorkflowAgentPreview?: () => void;
  previewWorkflowAgent?: WorkflowAgent;
  onConversationIdChange?: (conversationId?: string) => void;
  onRequestNewSession?: (
    handler: (routerState?: ConversationRouterState) => void,
  ) => void;
  showModelPickerButton?: boolean;
  showIncognitoButton?: boolean;
  shouldAutoFocusInput?: boolean;
}

export const ConversationAssistantPageContent = memo(
  ({
    variant = "default",
    shouldUseRouterNavigate,
    hideTitle,
    leftTitleActions,
    rightTitleActions,
    emptyState,
    getTransientSource,
    onCloseWorkflowAgentPreview,
    previewWorkflowAgent,
    onConversationIdChange,
    onRequestNewSession,
    showModelPickerButton = true,
    showIncognitoButton = true,
    shouldAutoFocusInput = true,
  }: ConversationAssistantPageContentProps) => {
    const isScopedStackChatEnabled = useFeatureFlagValueBool(
      "dash_web_2025_08_08_stack_chat_namespace_id",
    );
    const {
      conversationId,
      conversation,
      conversationType,
      isConversationLoading,
      stackChatDetails,
      workflowAgentConfig,
      scenarioMeta,
      navigateNewConversation,
      navigateNewWorkflowAgent,
    } = useRouteHandling(
      variant,
      onConversationIdChange,
      previewWorkflowAgent,
      shouldUseRouterNavigate,
    );
    const isSmallSize = useIsSmallSizeForSidebar();
    const [actionSurface, setActionSurface] = useState<string | undefined>(
      undefined,
    );
    const { currentSessionID } = useComposeCurrentSessionContext();
    const { logComposeEvent } = useComposeAnalyticsContext({
      actionSurfaceComponent: "compose_chat_pane",
      currentSessionID,
      actionSurface,
    });
    const [selectedModel, setSelectedModel] = useState<ModelId>("");
    const { manuallyAddedSources, setManuallyAddedSources, addSource } =
      useManuallyAddedSources();
    const { scopedSources } = useScopedSources();
    const [userMessageOptions, setUserMessageOptions] =
      useState<UserMessageOptions>({
        searchParams: new SearchParams({
          searchTarget: {
            value: new DashSearch(),
            case: "dashSearch",
          },
        }),
      });
    const getConversationAttachments = useGetConversationAttachments(
      scopedSources,
      manuallyAddedSources,
      getTransientSource,
      conversation?.messages || [],
      userMessageOptions?.browserContext?.selectedTabIDs,
    );
    useFssConversationActions(setManuallyAddedSources);
    useTranscribeFiles();

    const { storeState: storeInitialState, resetToInitialState } =
      useResetConversationState({
        conversationId,
        setUserMessageOptions,
      });

    const fssOpsHandling = useFSSOpsHandling();
    const { messagesToDisplay, templateSelectedMessage } = useFilteredMessages(
      conversation,
      workflowAgentConfig.workflowAgent?.id,
    );
    const { isLibraryOpen, activeTab, openLibrary, closeLibrary, switchTab } =
      useAIToolLibraryModal();
    const {
      progressComponent,
      isWaitingForResponse,
      isGeneratingResponse,
      showFollowUpSuggestionsSkeleton,
    } = useConversationProgress(conversationId, userMessageOptions);
    const writeHandling = useWriteHandling(
      conversationId,
      conversation,
      isWaitingForResponse,
      selectedModel,
      userMessageOptions,
      setUserMessageOptions,
      setManuallyAddedSources,
      logComposeEvent,
      getConversationAttachments,
      templateSelectedMessage,
      navigateNewConversation,
      openLibrary,
    );
    const { isConversationPageLoading, setIsConversationPageLoading } =
      useConversationPageLoadingStates(conversation, conversationId);
    const {
      isAddSourceModalOpen,
      setIsAddSourceModalOpen,
      handleClickAddSources,
      handleCloseAddSourcesModal,
    } = useAddSources(
      logComposeEvent,
      writeHandling.getAutoMessagesOnFirstSource,
      manuallyAddedSources,
      setManuallyAddedSources,
      setUserMessageOptions,
    );
    useWorkflowAgentHandling(
      variant,
      workflowAgentConfig,
      conversationId,
      setUserMessageOptions,
      setSelectedModel,
    );
    useConversationRouterState({
      agentPayload: writeHandling.draftConfig.agentPayload,
      chatSessionId: currentSessionID,
      conversationId,
      setSelectedVoiceID: writeHandling.draftConfig.setSelectedVoiceID,
      setSelectedTemplate: (template: SelectedTemplate) => {
        // When triggering a new chat session with a pre-selected template, drop the user into
        // empty chat flow to prompt for sources.
        setEmptyChatOption("write");
        writeHandling.draftConfig.onTemplateChange(template.id, template.name);
      },
      setUserMessageOptions,
      setManuallyAddedSources,
      setActionSurface,
      storeInitialState,
    });
    const agentPayload = useAgentPayload(
      writeHandling.draftConfig.agentPayload,
      fssOpsHandling.agentPayload,
    );

    const { closeOmniboxSearchSessionIfExists } =
      useCloseOmniboxSearchSession();

    // For home page/empty chat instruction flickering issues setting isSubmittingMessage
    // to true will resolve the issue
    const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
    useEffect(() => {
      if (!isWaitingForResponse) {
        setIsSubmittingMessage(false);
      }
    }, [isWaitingForResponse, setIsSubmittingMessage]);

    // Adapter function to bridge the old-style postUserMessage to the new service-conversation version for onboarding mode
    const postUserMessageAdapter = useCallback(
      async (params: { text?: string; rawPromptText?: string }) => {
        if (conversationId) {
          setIsSubmittingMessage(true);
          const attachments = await getConversationAttachments();
          postUserMessage({
            message: params.text || "",
            conversationId,
            attachments,
            source:
              getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
            searchParams: new SearchParams({
              searchTarget: {
                value: new NoSearch(),
                case: "noSearch",
              },
            }),
          });
        }
      },
      [conversationId, getConversationAttachments],
    );

    const { onboardingMode } = useOnboardingMode({
      addSource,
      logComposeEvent,
      postUserMessage: postUserMessageAdapter,
      setIsAddSourceModalOpen,
      sources: manuallyAddedSources,
    });
    useEffect(() => {
      if (
        onboardingMode &&
        userMessageOptions.searchParams?.searchTarget.case !== "noSearch"
      ) {
        // This makes sure that the suggestion chips do not trigger a search
        // This is needed because most users won't have contents that can answer the question when
        // they are first onboarded.
        setUserMessageOptions({
          ...userMessageOptions,
          searchParams: new SearchParams({
            searchTarget: {
              value: new NoSearch(),
              case: "noSearch",
            },
          }),
        });
      }
    }, [onboardingMode, userMessageOptions, setUserMessageOptions]);

    useLayoutEffect(() => {
      closeOmniboxSearchSessionIfExists("assist", {
        conversationId: conversation?.serverConversationId,
      });
    }, [conversation?.serverConversationId, closeOmniboxSearchSessionIfExists]);

    useEffect(() => {
      if (workflowAgentConfig.workflowAgent?.sources) {
        setUserMessageOptions(
          getUserMessageOptionsFromWorkflowAgent(
            workflowAgentConfig.workflowAgent,
          ),
        );
      }
    }, [workflowAgentConfig.workflowAgent, setUserMessageOptions]);

    useEffect(() => {
      // If the conversation is a stack chat and the scoped stack chat flag is enabled,
      // set the user message options to supply the namespace id on each turn. This is
      // done in the router state handler for new chats, but is necessary here to ensure
      // we also configured the options correctly when opening an existing conversation.
      if (isScopedStackChatEnabled && stackChatDetails?.namespaceId) {
        setUserMessageOptions({
          ...userMessageOptions,
          stackId: stackChatDetails.namespaceId,
        });
      }
    }, [isScopedStackChatEnabled, setUserMessageOptions, stackChatDetails]);

    const [emptyChatOption, setEmptyChatOption] = useState<
      ClickableEmptyChatInstructionsOption | undefined
    >(undefined);
    // Remove loading dependencies to prevent FSS component unmounting
    const showEmptyChatInstructions =
      (!conversation || !conversation?.messages?.length) &&
      !emptyChatOption &&
      manuallyAddedSources.length === 0 &&
      !isSubmittingMessage &&
      !onboardingMode;

    const [
      hideSuggestionsForCurrentAnswer,
      setHideSuggestionsForCurrentAnswer,
    ] = useState(false);

    const { aiToolLibraryModal } = useAIToolLibrary({
      onTemplateChange: writeHandling.draftConfig.onTemplateChange,
      onVoiceChange: writeHandling.draftConfig.onVoiceChange,
      onPostMessageWithVoice: writeHandling.draftConfig.onPostMessageWithVoice,
      onPostMessageWithTemplate:
        writeHandling.draftConfig.onPostMessageWithTemplate,
      setUserMessageOptions,
      setEmptyChatOption,
      logComposeEvent,
      currentTemplateID: writeHandling.draftConfig.selectedTemplate?.id,
      currentVoiceID: writeHandling.draftConfig.selectedVoiceID,
      markdownDraft: conversation?.markdownDraft || "",
      isLibraryOpen,
      activeTab,
      openLibrary,
      closeLibrary,
      switchTab,
    });

    const startingMessageNode = (
      <>
        {fssOpsHandling.header}
        <StartingMessageNode
          showEmptyChatInstructions={showEmptyChatInstructions}
          emptyState={emptyState}
          logComposeEvent={logComposeEvent}
          openLibrary={openLibrary}
          setEmptyChatOption={setEmptyChatOption}
          conversationId={conversationId}
          isConversationPageLoading={isConversationPageLoading}
          conversation={conversation}
          emptyChatOption={emptyChatOption}
          getWriteContextInputMessages={
            writeHandling.getWriteContextInputMessages
          }
          handleClickAddSources={handleClickAddSources}
          navigateNewWorkflowAgent={navigateNewWorkflowAgent}
          setUserMessageOptions={setUserMessageOptions}
          setHideSuggestionsForCurrentAnswer={
            setHideSuggestionsForCurrentAnswer
          }
          clientContext={userMessageOptions.clientContext}
        />
      </>
    );

    // Exists separately from the below because callbacks for mouse events cannot take parameters
    const handleStartNewSessionWithState = useCallback(
      (routerState?: ConversationRouterState) => {
        logger.debug("Starting a new session with state: ", routerState);
        setEmptyChatOption(undefined);
        setUserMessageOptions({
          searchParams: new SearchParams({
            searchTarget: {
              value: new DashSearch(),
              case: "dashSearch",
            },
          }),
        });
        if (!routerState) {
          resetToInitialState();
        }

        // Reset model to auto ("") when the user was previously in web search mode
        // (noSearch + "openai:gpt-4o-search-preview" model)
        const wasInWebSearchMode =
          userMessageOptions.searchParams?.searchTarget?.case === "noSearch" &&
          selectedModel === "openai:gpt-4o-search-preview";
        if (wasInWebSearchMode) {
          setSelectedModel("");
        }

        // Stay in incognito mode if the user was in incognito mode
        const wasInIncognitoMode = userMessageOptions.skipPersistence === true;
        if (wasInIncognitoMode) {
          setUserMessageOptions({
            ...userMessageOptions,
            skipPersistence: true,
          });
        }

        writeHandling.draftConfig.resetDraftConfigState();
        setIsConversationPageLoading(true);
        navigateNewConversation(routerState);
      },
      [
        userMessageOptions,
        selectedModel,
        writeHandling.draftConfig,
        setIsConversationPageLoading,
        navigateNewConversation,
        resetToInitialState,
      ],
    );
    // Deduplicate the above callback, just with no parameters
    const handleStartNewSession = useCallback(() => {
      handleStartNewSessionWithState();
    }, [handleStartNewSessionWithState]);

    useEffect(() => {
      onRequestNewSession?.(handleStartNewSessionWithState);
    }, [onRequestNewSession, handleStartNewSessionWithState]);

    const isFssStacksEnabled = useFeatureFlagValueBool(
      "dash_2025_06_24_fss_stacks",
    );
    const { pathname } = useLocation();
    // When isFssStacksEnabled=true and we are on stack pages (stack details or new stack page), we show chat sidebar on the page, so we won't display the chat panel.
    // We still want to display the chat panel when user access the old chat conversations via the chat page.
    const disableChatPanel =
      isFssStacksEnabled && pathname.includes(RoutePath.STACKS);

    const sideBarChildren = useMemo(() => {
      if (writeHandling.showWritePane) return undefined;

      if (stackChatDetails && !disableChatPanel) {
        return (
          <StackChatPanel
            chatSessionId={currentSessionID}
            lastUserMessage={stackChatDetails.lastUserMessage}
            onRefreshConversation={handleStartNewSessionWithState}
            serverConversationId={conversation?.serverConversationId}
            sourceDocumentIds={stackChatDetails.sourceDocumentIds}
            stackSharingId={stackChatDetails.sharingId}
          />
        );
      } else {
        return undefined;
      }
    }, [
      writeHandling.showWritePane,
      stackChatDetails,
      currentSessionID,
      handleStartNewSessionWithState,
      conversation?.serverConversationId,
      disableChatPanel,
    ]);

    // Controls whether the user can add additional sources to the conversation.
    // For legacy conversations, the inverse of this also controls whether all sources are hidden.
    const allowCustomSources =
      // Disallow for Stack Chats
      !stackChatDetails &&
      // Disallow for workflow agents (undefined is treated as false)
      (!workflowAgentConfig.workflowAgent ||
        workflowAgentConfig.workflowAgent.allowCustomSources === true) &&
      // Disallow for start page query suggestions and omnibox suggested questions
      conversationType !== ConversationType.AskFromSuggestion;

    if (!conversationId) {
      return null;
    }

    const sidePanelButtons = (
      <ConversationAssistantPageContentSidePanelButtons
        variant={variant}
        showEmptyChatInstructions={showEmptyChatInstructions}
        onCloseWorkflowAgentPreview={onCloseWorkflowAgentPreview}
        openLibrary={openLibrary}
        handleStartNewSessionWithState={handleStartNewSessionWithState}
        handleStartNewSession={handleStartNewSession}
        logComposeEvent={logComposeEvent}
        setEmptyChatOption={setEmptyChatOption}
        scenarioMeta={scenarioMeta}
        setUserMessageOptions={setUserMessageOptions}
      />
    );

    const chatGrid = (
      <>
        <ChatGrid
          logChatEvent={() => {
            // TODO logging
          }}
          rightPaneExpanded={writeHandling.showWritePane}
          setExpandRightPane={writeHandling.setShowWritePane}
          toggleView={writeHandling.handleToggleMobileView}
          isEditorView={writeHandling.isEditorViewOnMobile}
          hidden={isConversationLoading}
          sidePane={
            <SidePanelContainer
              leftTitleActions={leftTitleActions ?? sidePanelButtons}
              handleClickAdd={handleClickAddSources}
              showWorkflowAgent={false}
              rightTitleActions={rightTitleActions}
              hideTitle={
                variant === "workflow-agent-preview" ? true : hideTitle
              }
              sidebarChildren={sideBarChildren}
            >
              <ConversationChat
                variant={
                  variant === "workflow-agent-preview" ? "default" : variant
                }
                startingMessageNode={startingMessageNode}
                conversationId={conversationId}
                handleClickAdd={handleClickAddSources}
                handleCloseAddSourcesModal={handleCloseAddSourcesModal}
                isAddSourceModalOpen={isAddSourceModalOpen}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                agent={workflowAgentConfig.workflowAgent}
                showEmptyChatInstructions={showEmptyChatInstructions}
                messages={messagesToDisplay}
                progressComponent={progressComponent}
                isWaitingForResponse={isWaitingForResponse}
                agentPayload={agentPayload}
                isGeneratingResponse={isGeneratingResponse}
                getConversationAttachments={getConversationAttachments}
                userMessageOptions={userMessageOptions}
                setUserMessageOptions={setUserMessageOptions}
                logComposeEvent={logComposeEvent}
                setIsSubmittingMessage={setIsSubmittingMessage}
                // Don't provide the ability to add sources if this is a Stack Chat
                allowCustomSources={allowCustomSources}
                conversationType={conversationType}
                shouldHideSources={!allowCustomSources}
                showDraftGeneratedButton={
                  isSmallSize && writeHandling.showWritePane
                }
                onToggleMobileView={writeHandling.handleToggleMobileView}
                showFollowUpSuggestionsSkeleton={
                  showFollowUpSuggestionsSkeleton
                }
                showModelPickerButton={
                  showModelPickerButton && !workflowAgentConfig.workflowAgent
                }
                showContextPickerButton={!workflowAgentConfig.workflowAgent}
                showIncognitoButton={showIncognitoButton}
                hideSuggestionsForCurrentAnswer={
                  hideSuggestionsForCurrentAnswer
                }
                setHideSuggestionsForCurrentAnswer={
                  setHideSuggestionsForCurrentAnswer
                }
                shouldAutoFocusInput={shouldAutoFocusInput}
              />
            </SidePanelContainer>
          }
          rightPane={writeHandling.editorPane}
          showToggle={!!conversation?.markdownDraft}
        />
        {writeHandling.templateSettingsModal}
        {aiToolLibraryModal}
        <ChatDiagnosticsViewer />
      </>
    );

    return (
      <>
        {isConversationLoading && <DashSpinnerAutofitContainer />}
        {chatGrid}
      </>
    );
  },
);
ConversationAssistantPageContent.displayName =
  "ConversationAssistantPageContent";
